import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Boxes,
  CalendarClock,
  CheckCircle2,
  Loader2,
  Octagon,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";
import GlowCard from "../components/GlowCard";
import KPITile from "../components/KPITile";
import StatusBadge from "../components/StatusBadge";
import { listProjects } from "../Projects/api";
import { VerityProject, reviewTypeOf } from "../Projects/types";
import { listPackages } from "./api";
import { contentKey, counted, staggered, useIntro } from "../components/intro";
import { WorkArea, WorkPackage } from "./types";

/**
 * The AWP dashboard — David's layout, counted from what the module actually holds.
 *
 * ZEROS, NOT PLACEHOLDERS. Reviews, findings, constraints and vendor data are not built yet, so the
 * tiles and charts that count them read 0 — which is true: no package has been reviewed, nothing has
 * been found, no constraint is open. A "coming soon" note in their place would hide the layout the
 * numbers will fill. Everything that CAN be counted already is real: the packages, their work areas
 * and hours, and what falls due in the next four weeks.
 *
 * Per project, as David's is: the unit is chosen at the top.
 */

/** Single-series magnitude — the engineering dashboard's one hue, so the two read alike. */
const SINGLE = "#0097ab";

const DAY = 24 * 60 * 60 * 1000;
const WINDOW_DAYS = 28;

/** The CWP handover funnel's stages. Nothing is reviewed yet, so every CWP is still in Draft. */
const FUNNEL = ["Draft", "In Review", "Ready for Handover", "Handed Over"] as const;
const CHECK_TYPES = ["Completeness", "Consistency", "Conformance", "Readiness"] as const;
const DELIVERY = ["On time", "At risk", "Late"] as const;

interface Bars {
  label: string;
  count: number;
  note?: string;
}

/** Horizontal bars on one shared scale — the engineering dashboard's stage chart. */
/**
 * Horizontal bars on one shared scale — the engineering dashboard's stage chart — growing in on the
 * dashboard's shared clock. `t` is the run's progress; `from` is this chart's place in the entrance,
 * so a chart further down starts a beat after the ones above it.
 */
const BarRows = ({ rows, unit, t, from }: { rows: Bars[]; unit: string; t: number; from: number }) => {
  const max = Math.max(...rows.map((r) => r.count), 1);
  return (
    <div className="vr-stage-chart">
      {rows.map((r, i) => {
        const p = staggered(t, from + i);
        return (
          <div key={r.label} className="vr-stage-row">
            <span className="vr-stage-name">{r.label}</span>
            <span className="vr-stage-track">
              <span
                className="vr-stage-fill"
                style={{ width: `${(r.count / max) * 100 * p}%`, background: SINGLE }}
                title={`${r.count} ${unit}${r.count === 1 ? "" : "s"} — ${r.label}${r.note ? ` (${r.note})` : ""}`}
              />
            </span>
            <span className="vr-stage-num">{Math.round(r.count * p)}</span>
          </div>
        );
      })}
    </div>
  );
};

const AwpDashboard = () => {
  const [projects, setProjects] = useState<VerityProject[] | null>(null);
  const [projectId, setProjectId] = useState<string>("");
  const [packages, setPackages] = useState<WorkPackage[] | null>(null);
  const [cwas, setCwas] = useState<WorkArea[]>([]);
  const [error, setError] = useState<string | null>(null);

  // The reviewer's own AWP projects — the same list their Projects screen shows.
  useEffect(() => {
    void (async () => {
      try {
        const mine = (await listProjects("mine")).filter((p) => reviewTypeOf(p) === "awp");
        setProjects(mine);
        setProjectId((cur) => cur || mine[0]?.id || "");
      } catch (err) {
        setError(err instanceof Error ? err.message : "Could not load your projects.");
        setProjects([]);
      }
    })();
  }, []);

  const load = useCallback(async () => {
    if (!projectId) return;
    try {
      setError(null);
      setPackages(null);
      const result = await listPackages(projectId);
      setPackages(result.packages);
      setCwas(result.cwas);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load the packages.");
      setPackages([]);
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  const data = useMemo(() => {
    const all = packages ?? [];
    const cwps = all.filter((p) => p.type === "CWP");
    const pwps = all.filter((p) => p.type === "PWP");
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const horizon = today.getTime() + WINDOW_DAYS * DAY;

    // What falls due in the window: a CWP or EWP by its need date, a PWP by its required-on-site.
    // Nothing has been reviewed Ready yet, so everything due counts as due-and-not-ready.
    const due = all
      .map((p) => ({ pkg: p, date: p.type === "PWP" ? p.rosDate : p.needDate }))
      .filter((d): d is { pkg: WorkPackage; date: string } => !!d.date)
      .map((d) => ({ ...d, at: new Date(`${d.date}T00:00:00`).getTime() }))
      .filter((d) => d.at <= horizon)
      .sort((a, b) => a.at - b.at);

    const sortedAreas = [...cwas].sort(
      (a, b) =>
        (a.sequence ?? Number.MAX_SAFE_INTEGER) - (b.sequence ?? Number.MAX_SAFE_INTEGER) ||
        a.code.localeCompare(b.code),
    );
    const byArea: Bars[] = sortedAreas.map((a) => {
      const inArea = all.filter((p) => p.cwaId === a.id);
      const hours = inArea.reduce((n, p) => n + (p.hours ?? 0), 0);
      return {
        label: a.name ? `${a.code} · ${a.name}` : a.code,
        count: inArea.length,
        note: hours ? `${hours.toLocaleString()} h` : undefined,
      };
    });
    const unassigned = all.filter((p) => !p.cwaId || !cwas.some((a) => a.id === p.cwaId)).length;
    if (unassigned) byArea.push({ label: "No work area", count: unassigned });

    return {
      all,
      counts: {
        CWP: cwps.length,
        EWP: all.filter((p) => p.type === "EWP").length,
        PWP: pwps.length,
      },
      funnel: FUNNEL.map((stage) => ({ label: stage, count: stage === "Draft" ? cwps.length : 0 })),
      byArea,
      due,
      overdue: due.filter((d) => d.at < today.getTime()).length,
      longLead: pwps.filter((p) => p.longLead),
    };
  }, [packages, cwas]);

  // THE ENTRANCE — the engineering dashboard's: tiles count up on one shared clock, each a beat
  // behind the one before, then the charts grow in. Keyed on the project and what its packages say,
  // so switching project or a real change replays it and a Refresh returning the same data does not.
  const introKey = useMemo(
    () =>
      packages === null
        ? null
        : `${projectId}#${contentKey(packages, (p) => `${p.id}:${p.updatedAt ?? ""}`)}|${cwas.length}`,
    [packages, projectId, cwas.length],
  );
  const t = useIntro(introKey);
  const pct = (n: number) => `${Math.round(n)}%`;

  const project = (projects ?? []).find((p) => p.id === projectId);

  if (projects === null) {
    return (
      <div className="vr-page">
        <div className="vr-empty">
          <Loader2 size={16} className="vr-spin-inline" /> Loading the dashboard…
        </div>
      </div>
    );
  }

  return (
    <div className="vr-page vr-page-fixed">
      <div className="vr-page-head">
        <div>
          <h1 className="vr-h-page">AWP Dashboard</h1>
          <p>
            {project
              ? `${project.name} · ${project.supplier} — quality gate before handover`
              : "No AWP projects are assigned to you yet."}
          </p>
        </div>
        <div className="vr-head-actions">
          {projects.length > 0 && (
            <select
              className="vr-select vr-awp-filter"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              aria-label="Project"
            >
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          )}
          <button className="vr-rowbtn" onClick={() => void load()} disabled={!projectId}>
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </div>

      {error && <div className="vr-form-error">{error}</div>}

      {projectId && packages === null && (
        <div className="vr-empty">
          <Loader2 size={16} className="vr-spin-inline" /> Loading packages…
        </div>
      )}

      {projectId && packages !== null && (
        <div className="vr-scroll-region">
          <div className="vr-grid vr-grid-6">
            <KPITile
              icon={Boxes}
              label="Packages"
              value={counted(data.all.length, staggered(t, 0))}
              trend={`${data.counts.CWP} CWP · ${data.counts.EWP} EWP · ${data.counts.PWP} PWP`}
              trendUp
            />
            <KPITile icon={ShieldCheck} label="Reviewed" value={counted(0, staggered(t, 1), pct)} trend={`0 of ${data.all.length}`} trendUp />
            <KPITile icon={CheckCircle2} label="Ready" value={counted(0, staggered(t, 2), pct)} trend="0 packages" trendUp />
            <KPITile icon={Octagon} label="Open Critical Findings" value={counted(0, staggered(t, 3))} />
            <KPITile icon={AlertTriangle} label="Open Constraints" value={counted(0, staggered(t, 4))} />
            <KPITile
              icon={CalendarClock}
              label="Due ≤ 4 Weeks, Not Ready"
              value={counted(data.due.length, staggered(t, 5))}
              trend={data.overdue ? `${data.overdue} already past` : undefined}
            />
          </div>

          {/* What falls due in the window, soonest first — David's "due for submission" strip. */}
          <GlowCard className="vr-chart-card" hover={false}>
            <div className="vr-card-head">
              <h3>Due in the next 4 weeks</h3>
            </div>
            {data.due.length === 0 ? (
              <div className="vr-empty">
                Nothing due in the next 4 weeks. Need and ROS dates are set on each package's ⚡.
              </div>
            ) : (
              <div className="vr-awp-due">
                {data.due.map(({ pkg, date }, i) => (
                  <span
                    key={pkg.id}
                    className="vr-awp-due-item"
                    // Each chip settles in a beat after the tiles, one after another.
                    style={{
                      opacity: staggered(t, 6 + i),
                      transform: `translateY(${(1 - staggered(t, 6 + i)) * 6}px)`,
                    }}
                  >
                    <span className={`vr-pkg-type vr-pkg-${pkg.type.toLowerCase()}`}>{pkg.type}</span>
                    <strong>{pkg.packageNo}</strong>
                    <span className="vr-cell-code">{date}</span>
                    <StatusBadge status="info" label="Not reviewed" />
                  </span>
                ))}
              </div>
            )}
          </GlowCard>

          <div className="vr-dash-row vr-dash-row-half">
            <GlowCard className="vr-chart-card" hover={false}>
              <div className="vr-card-head">
                <h3>CWP Handover Funnel</h3>
              </div>
              <BarRows rows={data.funnel} unit="CWP" t={t} from={6} />
            </GlowCard>

            <GlowCard className="vr-chart-card" hover={false}>
              <div className="vr-card-head">
                <h3>Open Findings by Check Type</h3>
              </div>
              <BarRows rows={CHECK_TYPES.map((label) => ({ label, count: 0 }))} unit="finding" t={t} from={7} />
            </GlowCard>
          </div>

          <div className="vr-dash-row vr-dash-row-half">
            <GlowCard className="vr-chart-card" hover={false}>
              <div className="vr-card-head">
                <h3>Packages by Work Area</h3>
              </div>
              {data.byArea.length === 0 ? (
                <div className="vr-empty">No work areas on this project yet.</div>
              ) : (
                <BarRows rows={data.byArea} unit="package" t={t} from={8} />
              )}
            </GlowCard>

            <GlowCard className="vr-chart-card" hover={false}>
              <div className="vr-card-head">
                <h3>PWP Deliveries vs ROS</h3>
              </div>
              <BarRows rows={DELIVERY.map((label) => ({ label, count: 0 }))} unit="PWP" t={t} from={9} />
            </GlowCard>
          </div>

          <GlowCard className="vr-chart-card" hover={false}>
            <div className="vr-card-head">
              <h3>Vendor Data and Long-Lead Items</h3>
            </div>
            <div className="vr-grid vr-grid-3">
              <KPITile icon={CalendarClock} label="Outstanding" value={counted(0, staggered(t, 10))} />
              <KPITile icon={AlertTriangle} label="Overdue" value={counted(0, staggered(t, 11))} />
              <KPITile icon={CheckCircle2} label="Received" value={counted(0, staggered(t, 12))} />
            </div>
            <div className="vr-table-scroll">
              <table className="vr-table" style={{ minWidth: 0 }}>
                <thead>
                  <tr>
                    <th>Long-lead item</th>
                    <th>PWP</th>
                    <th>ROS</th>
                    <th>PO</th>
                  </tr>
                </thead>
                <tbody>
                  {data.longLead.map((p) => (
                    <tr key={p.id}>
                      <td>{p.title}</td>
                      <td className="vr-cell-dim">{p.packageNo}</td>
                      <td className="vr-cell-dim">{p.rosDate || "—"}</td>
                      <td className="vr-cell-dim">—</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {data.longLead.length === 0 && <div className="vr-empty">No PWPs flagged long-lead.</div>}
            </div>
          </GlowCard>
        </div>
      )}
    </div>
  );
};

export default AwpDashboard;
